import React, { FC, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { TerminateContractViewState } from './index'
import {
  Beneficiary,
  Contract,
  ContractTenant,
  FeatureFlag,
  UserMetadata,
} from '../../../common/validations'
import { ContractOperation } from '../../../common/types'
import { TerminateContractForm } from './TerminateForm/TerminateContractForm'
import { ModifyContractForm } from './ModificationForm/ModifyContractForm'
import { TerminateContractErrorHandler } from './TerminateContractErrorHandler'
import { ContractModificationErrorHandler } from './ContractModificationErrorHandler'

import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'

interface FormProps {
  view: TerminateContractViewState
  setView: (view: TerminateContractViewState) => void
  tenants: ContractTenant[]
  beneficiaries: Beneficiary[]
  contract: Contract
  userMetadata: UserMetadata
  featureFlags?: FeatureFlag
  terminateTenants: number[]
  setTerminateTenants: React.Dispatch<React.SetStateAction<number[]>>
}

export const Form: FC<FormProps> = ({
  view,
  setView,
  tenants,
  beneficiaries,
  contract,
  userMetadata,
  featureFlags,
  terminateTenants,
  setTerminateTenants,
}) => {
  const { contractModification } = useModuleConfig()
  const [params] = useSearchParams()
  const [tenantSelectionConfirmed, setTenantSelectionConfirmed] =
    React.useState(false)

  // The strong authentication callback reloads the page, so the tenant
  // selection is gone and the completed operation has to be read from the URL.
  const confirmedOperation = params.get('operation')

  const isTerminateMode = (() => {
    switch (confirmedOperation) {
      case ContractOperation.TERMINATION:
        return true
      case ContractOperation.MODIFICATION:
        return false
      default:
        return (
          tenants.length === terminateTenants.length || !contractModification
        )
    }
  })()

  useEffect(() => {
    setTenantSelectionConfirmed(false)
  }, [isTerminateMode])

  return isTerminateMode ? (
    <TerminateContractErrorHandler view="form">
      <TerminateContractForm
        view={view}
        setView={setView}
        terminateTenants={terminateTenants}
        setTerminateTenants={setTerminateTenants}
        tenants={tenants}
        beneficiaries={beneficiaries}
        contract={contract}
        userMetadata={userMetadata}
        featureFlags={featureFlags}
        tenantSelectionConfirmed={tenantSelectionConfirmed}
        setTenantSelectionConfirmed={setTenantSelectionConfirmed}
      />
    </TerminateContractErrorHandler>
  ) : (
    <ContractModificationErrorHandler view="form">
      <ModifyContractForm
        contract={contract}
        tenants={tenants}
        terminateTenants={terminateTenants}
        setTerminateTenants={setTerminateTenants}
        featureFlags={featureFlags}
        userMetadata={userMetadata}
        tenantSelectionConfirmed={tenantSelectionConfirmed}
        setTenantSelectionConfirmed={setTenantSelectionConfirmed}
      />
    </ContractModificationErrorHandler>
  )
}
